import { CityPassParams } from 'services/cityPass';
import { Customer } from 'services/customer';
import { PaymentDevice } from 'services/hardware';
import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'cart';

// Action Types
export const types = {
  RESERVE_TICKETS_ASYNC: createAsyncTypes(`${KEY}/RESERVE_TICKETS_ASYNC`),
  RESERVE_MEMBERSHIP_ASYNC: createAsyncTypes(`${KEY}/RESERVE_MEMBERSHIP_ASYNC`),
  APPLY_DONATION_ASYNC: createAsyncTypes(`${KEY}/APPLY_DONATION_ASYNC`),
  APPLY_PROMO_CODE_ASYNC: createAsyncTypes(`${KEY}/APPLY_PROMO_CODE_ASYNC`),
  APPLY_GIFT_VOUCHER_ASYNC: createAsyncTypes(`${KEY}/APPLY_GIFT_VOUCHER_ASYNC`),
  PURCHASE_CART_ASYNC: createAsyncTypes(`${KEY}/PURCHASE_CART_ASYNC`),
  REMOVE_TICKETS_FROM_CART: createAsyncTypes(`${KEY}/REMOVE_TICKETS_FROM_CART`),
  REMOVE_DONATION_ASYNC: createAsyncTypes(`${KEY}/REMOVE_DONATION_ASYNC`),
  RELEASE_CART_ASYNC: createAsyncTypes(`${KEY}/RELEASE_CART_ASYNC`),
  SET_DONATION_SOURCE: `${KEY}/SET_DONATION_SOURCE`,
  RESET_CART: `${KEY}/RESET_CART`,
  RESET_PURCHASE_MESSAGE: `${KEY}/RESET_PURCHASE_MESSAGE`,
  RESET_PROMO_CODE: `${KEY}/RESET_PROMO_CODE`,
  RESET_GIFT_VOUCHER: `${KEY}/RESET_GIFT_VOUCHER`,
  RESET_CART_ERROR: `${KEY}/RESET_CART_ERROR`,
  RESET_DONATION_ERROR: `${KEY}/RESET_DONATION_ERROR`
};

export interface SeatInfo {
  row: string;
  seat: string;
  section: string;
}

export interface MembershipDetails {
  /** name of product; used to fetch the associated configuration */
  membershipName: string;
  /** 'renewal' or 'upgrade'; used for different messaging on the cart view */
  action: string;
  /** current level of the active membership */
  currentLevel: string;
  /** new level in the cart that will be the result of a renewal or upgrade */
  newLevel: string;
  /** date that the new membership level will start */
  newLevelStartDate: string;
  /** date that the new membership level will end */
  newLevelEndDate: string;
}

export type SubProduct = Omit<Product, 'subProducts'>;

export interface Item {
  id: string;
  typeId: string;
  type: string;
  unitPrice: number;
  quantity: number;
  priceWithFees?: number;
  seatInfo?: SeatInfo[];
  fees?: number;
  isCityPass?: boolean;
  removeParams: any;
}

export interface Product {
  name: string;
  date: Date | string;
  location: string;
  type: string;
  firstDate?: Date | string;
  lastDate?: Date | string;
  tier?: string;
  isGA?: boolean;
  genre?: string;
  quantity: number;
  subTotal?: number;
  items?: Item[];
  membershipDetails?: MembershipDetails;
  subProducts?: SubProduct[];
  /** Can be used to remove the product from the cart; props vary by integration & product type */
  removeParams?: any;
}

export interface Cart {
  checkoutParams: any;
  releaseParams: any;
  addMoreParams?: any;
  donationParams?: any;
  applyPromoParams?: any;
  customerParams?: any;
  paymentIntentParams?: any;
  authorizeParams?: any;
  expiration: Date | string;
  feeTotal: number;
  products: Product[];
  total: number;
  subTotal?: number;
  quantity?: number;
  discount?: number;
}

export interface ReserveTicketsPayload {
  /** list of tickets/items to reserve */
  items: any[];
  /** existing cart details if adding more tickets */
  addMoreParams?: any;
  /** CityPass details if tickets were validated */
  cityPassParams?: CityPassParams;
  /** membership details if member logged in */
  membershipParams?: any;
}

export interface ReserveMembershipPayload {
  types: {
    fields?: any;
    reserveParams: any;
  }[];
  // existing cart details if adding more tickets
  addMoreParams?: any;
  /** CityPass details if tickets were validated */
  cityPassParams?: CityPassParams;
  // membership details if member logged in
  membershipParams?: any;
}

export interface RemoveTicketsPayload {
  /** list of tickets/items to remove */
  items: any[];
  /** CityPass details if tickets were validated */
  cityPassParams?: CityPassParams;
  /** membership details if member logged in */
  membershipParams?: any;
}

export interface PurchasePayload {
  data: PaymentDevice;
  checkoutParams: any;
  customer?: Customer;
  /** CityPass details if tickets were validated */
  cityPassParams?: CityPassParams & {
    usageDate: string;
  };
}

export interface PurchaseCartResponse {
  deliveryParams: any;
  transactionId: string;
  orderId: string;
  transactionAmount: number;
  cardType?: string;
  cardNumber?: string;
}

export interface PromoCode {
  promoCode?: string;
  loading: boolean;
  error: string | undefined;
  processed: boolean;
}

export interface GiftVoucher {
  voucherCode?: string;
  amount?: number;
  loading: boolean;
  error: string | undefined;
  processed: boolean;
}

export interface Donation {
  source: string | null;
  error: string | undefined;
}

export interface CartState {
  readonly cart: Cart | null;
  readonly purchased: boolean;
  readonly reserving: boolean;
  readonly loading: boolean;
  readonly error: undefined | string;
  readonly checkoutCart: PurchaseCartResponse | null;
  readonly purchaseError: string | undefined;
  readonly promoCode: PromoCode;
  readonly giftVoucher: GiftVoucher;
  readonly donation: Donation;
}

export interface ReserveTicketsAsyncRequest {
  type: typeof types.RESERVE_TICKETS_ASYNC.REQUEST;
  payload: any[];
}

export interface ReserveTicketsAsyncError {
  type: typeof types.RESERVE_TICKETS_ASYNC.ERROR;
  payload: string;
}

export interface ReserveTicketsAsyncPending {
  type: typeof types.RESERVE_TICKETS_ASYNC.PENDING;
  payload: boolean;
}

export interface ReserveTicketsAsyncSuccess {
  type: typeof types.RESERVE_TICKETS_ASYNC.SUCCESS;
  payload: Cart;
}

export interface ReserveMembershipAsyncRequest {
  type: typeof types.RESERVE_MEMBERSHIP_ASYNC.REQUEST;
  payload: ReserveMembershipPayload;
}

export interface ReserveMembershipAsyncError {
  type: typeof types.RESERVE_MEMBERSHIP_ASYNC.ERROR;
  payload: string;
}

export interface ReserveMembershipAsyncSuccess {
  type: typeof types.RESERVE_MEMBERSHIP_ASYNC.SUCCESS;
  payload: Cart;
}

export interface ReserveMembershipAsyncPending {
  type: typeof types.RESERVE_MEMBERSHIP_ASYNC.PENDING;
  payload: boolean;
}

export interface ReleaseCartAsyncRequest {
  type: typeof types.RELEASE_CART_ASYNC.REQUEST;
  payload: any;
}

export interface ReleaseCartAsyncError {
  type: typeof types.RELEASE_CART_ASYNC.ERROR;
  payload: string;
}

export interface ReleaseCartAsyncPending {
  type: typeof types.RELEASE_CART_ASYNC.PENDING;
  payload: boolean;
}

export interface ReleaseCartAsyncSuccess {
  type: typeof types.RELEASE_CART_ASYNC.SUCCESS;
}

export interface PurchaseCartAsyncRequest {
  type: typeof types.PURCHASE_CART_ASYNC.REQUEST;
}

export interface PurchaseCartAsyncError {
  type: typeof types.PURCHASE_CART_ASYNC.ERROR;
  payload: string;
}

export interface PurchaseCartAsyncPending {
  type: typeof types.PURCHASE_CART_ASYNC.PENDING;
  payload: boolean;
}

export interface PurchaseCartAsyncSuccess {
  type: typeof types.PURCHASE_CART_ASYNC.SUCCESS;
  payload: PurchaseCartResponse;
}

export interface ResetCart {
  type: typeof types.RESET_CART;
}

export interface ApplyPromoCodeAsyncRequest {
  type: typeof types.APPLY_PROMO_CODE_ASYNC.REQUEST;
  payload: string;
}

export interface ApplyPromoCodeAsyncError {
  type: typeof types.APPLY_PROMO_CODE_ASYNC.ERROR;
  payload: string;
}

export interface ApplyPromoCodeAsyncPending {
  type: typeof types.APPLY_PROMO_CODE_ASYNC.PENDING;
  payload: boolean;
}

export interface ApplyPromoCodeAsyncSuccess {
  type: typeof types.APPLY_PROMO_CODE_ASYNC.SUCCESS;
  payload: {
    processed: boolean;
    updatedCart: Cart;
  };
}

export interface ApplyGiftVoucherAsyncRequest {
  type: typeof types.APPLY_GIFT_VOUCHER_ASYNC.REQUEST;
  payload: {
    giftCertificateNumber: string;
  };
}

export interface ApplyGiftVoucherAsyncError {
  type: typeof types.APPLY_GIFT_VOUCHER_ASYNC.ERROR;
  payload: GiftVoucher;
}

export interface ApplyGiftVoucherAsyncPending {
  type: typeof types.APPLY_GIFT_VOUCHER_ASYNC.PENDING;
  payload: boolean;
}

export interface ApplyGiftVoucherAsyncSuccess {
  type: typeof types.APPLY_GIFT_VOUCHER_ASYNC.SUCCESS;
  payload: GiftVoucher;
}

export interface RemoveTicketsAsyncRequest {
  type: typeof types.REMOVE_TICKETS_FROM_CART.REQUEST;
  /** List of removeParams, which varies by integration */
  payload: any[];
}

export interface RemoveTicketsAsyncError {
  type: typeof types.REMOVE_TICKETS_FROM_CART.ERROR;
  payload: string;
}

export interface RemoveTicketsAsyncPending {
  type: typeof types.REMOVE_TICKETS_FROM_CART.PENDING;
  payload: boolean;
}

export interface RemoveTicketsAsyncSuccess {
  type: typeof types.REMOVE_TICKETS_FROM_CART.SUCCESS;
  payload: Cart;
}

export interface ApplyDonationAsyncRequest {
  type: typeof types.APPLY_DONATION_ASYNC.REQUEST;
  payload: number;
}

export interface ApplyDonationAsyncError {
  type: typeof types.APPLY_DONATION_ASYNC.ERROR;
  payload: string;
}

export interface ApplyDonationAsyncPending {
  type: typeof types.APPLY_DONATION_ASYNC.PENDING;
  payload: boolean;
}

export interface ApplyDonationAsyncSuccess {
  type: typeof types.APPLY_DONATION_ASYNC.SUCCESS;
  payload: Cart;
}

export interface RemoveDonationAsyncRequest {
  type: typeof types.REMOVE_DONATION_ASYNC.REQUEST;
  /** removeParams of the cart item, which varies by integration */
  payload: any;
}

export interface RemoveDonationAsyncError {
  type: typeof types.REMOVE_DONATION_ASYNC.ERROR;
  payload: string;
}

export interface RemoveDonationAsyncPending {
  type: typeof types.REMOVE_DONATION_ASYNC.PENDING;
  payload: boolean;
}

export interface RemoveDonationAsyncSuccess {
  type: typeof types.REMOVE_DONATION_ASYNC.SUCCESS;
  payload: Cart;
}

export interface SetDonationSource {
  type: typeof types.SET_DONATION_SOURCE;
  payload: string | null;
}

export type CartActionTypes =
  | ReserveTicketsAsyncRequest
  | ReserveTicketsAsyncPending
  | ReserveTicketsAsyncError
  | ReserveTicketsAsyncSuccess
  | ReserveMembershipAsyncRequest
  | ReserveMembershipAsyncPending
  | ReserveMembershipAsyncError
  | ReserveMembershipAsyncSuccess
  | ReleaseCartAsyncRequest
  | ReleaseCartAsyncPending
  | ReleaseCartAsyncError
  | ReleaseCartAsyncSuccess
  | PurchaseCartAsyncRequest
  | PurchaseCartAsyncPending
  | PurchaseCartAsyncError
  | PurchaseCartAsyncSuccess
  | ResetCart
  | ApplyPromoCodeAsyncRequest
  | ApplyGiftVoucherAsyncRequest
  | ApplyGiftVoucherAsyncError
  | ApplyGiftVoucherAsyncPending
  | ApplyGiftVoucherAsyncSuccess
  | RemoveTicketsAsyncRequest
  | RemoveTicketsAsyncError
  | RemoveTicketsAsyncPending
  | RemoveTicketsAsyncSuccess
  | ApplyDonationAsyncRequest
  | ApplyDonationAsyncError
  | ApplyDonationAsyncPending
  | ApplyDonationAsyncSuccess
  | RemoveDonationAsyncRequest
  | RemoveDonationAsyncError
  | RemoveDonationAsyncPending;
